import { useContext, useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import CertificateTemplateChooser from './components/CertificateTemplateChooser'
import CertificateComposer from './components/CertificateComposer'
import CertificateResult from './components/CertificateResult'
import ExistingCertificatesList from './components/ExistingCertificatesList'
import PatientPickerStep from './components/PatientPickerStep'

const CREATE_STEP = { TEMPLATE: 'template', COMPOSE: 'compose', RESULT: 'result' }

/**
 * Orchestrates the medical-document flow: optional patient picker (header "+" flow) -> two tabs
 * (Create / Existing). Create: template chooser -> composer -> server PDF result.
 */
export default function MedicalCertificateModal({
  open,
  mode = 'create',
  patientContext,
  clinicId,
  clinicBrandId,
  clinicName,
  onClose,
}) {
  const mentorProfileContext = useContext(MentorProfileContext)
  const mentorProfile = mentorProfileContext?.mentorProfileData

  const [patient, setPatient] = useState(null)
  const [activeTab, setActiveTab] = useState('create')
  const [createStep, setCreateStep] = useState(CREATE_STEP.TEMPLATE)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [generatedCertificate, setGeneratedCertificate] = useState(null)

  useEffect(() => {
    if (!open) return
    setPatient(patientContext?.patientId ? patientContext : null)
    setActiveTab(mode === 'view' ? 'view' : 'create')
    setCreateStep(CREATE_STEP.TEMPLATE)
    setSelectedTemplate(null)
    setGeneratedCertificate(null)
  }, [open, mode, patientContext])

  const resetCreateFlow = () => {
    setCreateStep(CREATE_STEP.TEMPLATE)
    setSelectedTemplate(null)
    setGeneratedCertificate(null)
  }

  const handleTabChange = (_e, value) => {
    setActiveTab(value)
    if (value === 'create') resetCreateFlow()
  }

  const renderBody = () => {
    if (!patient?.patientId) {
      return (
        <PatientPickerStep
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          onSelect={(p) => setPatient(p)}
        />
      )
    }

    if (activeTab === 'view') {
      return (
        <ExistingCertificatesList
          patientId={patient.patientId}
          onCreateNew={() => {
            resetCreateFlow()
            setActiveTab('create')
          }}
        />
      )
    }

    if (createStep === CREATE_STEP.TEMPLATE) {
      return (
        <CertificateTemplateChooser
          clinicId={clinicId}
          discipline={patient.discipline}
          onSelect={(template) => {
            setSelectedTemplate(template)
            setCreateStep(CREATE_STEP.COMPOSE)
          }}
        />
      )
    }

    if (createStep === CREATE_STEP.COMPOSE) {
      return (
        <CertificateComposer
          template={selectedTemplate}
          patient={patient}
          mentorProfile={mentorProfile}
          clinicName={clinicName}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          bookedEventId={patient.bookedEventId}
          onBack={() => setCreateStep(CREATE_STEP.TEMPLATE)}
          onGenerated={(cert) => {
            setGeneratedCertificate(cert)
            setCreateStep(CREATE_STEP.RESULT)
          }}
        />
      )
    }

    return (
      <CertificateResult
        certificate={generatedCertificate}
        onCreateAnother={resetCreateFlow}
        onClose={onClose}
      />
    )
  }

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Create a medical document</Typography>
          {patient?.patientName && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{patient.patientName}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {patient?.patientId && (
        <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab value="create" label="Create" sx={{ textTransform: 'none' }} />
            <Tab value="view" label="Existing certificates" sx={{ textTransform: 'none' }} />
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ minHeight: 420 }}>{renderBody()}</DialogContent>
    </Dialog>
  )
}
